/**
 * AssetPreviewModal Component
 * Modal for previewing assets with detailed information
 * Enhanced with dimension and aspect ratio display
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Asset,
  getAssetType,
  formatFileSize,
  formatDimensions,
  formatAspectRatio,
  formatDuration,
} from "@/types/asset.types";
import { AssetFilename } from "./AssetFilename";
import { VideoPlayer } from "./VideoPlayer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Download,
  Trash2,
  FileVideo,
  Copy,
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  AlertCircle,
  Lock,
  Workflow,
} from "lucide-react";
import {
  useAssetPreview,
  type AssetPreviewError,
} from "@/hooks/useAssetPreview";
import { WaveformMini } from "@/components/audio/WaveformMini";
import { formatDateTime } from "@/utils/datetime";
import { downloadAsset } from "@/utils/assets";
import { useAssetMetadata } from "@/hooks/useAssetMetadata";
import { useSignedCookies } from "@/hooks/useSignedCookies";
import { buildCloudFrontUrl } from "@/components/timeline/media/ChunkedVideoManager";
import { resolveThumbnailUrl } from "@/utils/derivedAssetUrls";
import { listDerivedAssets } from "@/services/api/sdk.gen";

/** Map mimeTypes to user-friendly format names */
const VIDEO_FORMAT_LABELS: Record<string, string> = {
  "video/quicktime": "MOV (QuickTime)",
  "video/x-matroska": "MKV (Matroska)",
  "video/mp4": "MP4",
  "video/webm": "WebM",
};

interface AssetPreviewModalProps {
  asset: Asset | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: (asset: Asset) => void;
  showDelete?: boolean;
  /** Optional label to display (e.g., "Output #3") */
  outputLabel?: string;
  /** Optional output version number */
  outputVersion?: number;
  /** Optional text content to display (for text outputs without assets) */
  textContent?: string;
  /** Optional array of assets for multi-image navigation */
  assets?: Asset[];
  /** Initial index when showing multiple assets */
  initialAssetIndex?: number;
  /** Project ID fallback (used when asset.projectId is null) */
  projectId?: string;
  /**
   * Optional asset ID for deep-linking support.
   * When provided and `asset` is null, the modal will fetch the asset by ID.
   */
  assetId?: string | null;
}

/** Type colors for media icons */
const TYPE_COLORS: Record<string, { color: string; rgb: string }> = {
  video: {
    color: "var(--color-primitive-pink-500)",
    rgb: "var(--color-primitive-pink-500-rgb)",
  },
  audio: {
    color: "var(--color-primitive-blue-500)",
    rgb: "var(--color-primitive-blue-500-rgb)",
  },
  image: {
    color: "var(--color-primitive-violet-500)",
    rgb: "var(--color-primitive-violet-500-rgb)",
  },
  text: {
    color: "var(--color-primitive-amber-600)",
    rgb: "var(--color-primitive-amber-600-rgb)",
  },
  default: {
    color: "var(--color-primitive-neutral-300)",
    rgb: "var(--color-primitive-neutral-300-rgb)",
  },
};

/** Resolve both opaque and translucent type colours from the same token. */
function getTypeColors(type: string) {
  return TYPE_COLORS[type] || TYPE_COLORS.default;
}

/** Get type icon for media type - color matches type */
function getTypeIcon(type: string): React.ReactNode {
  const { color } = getTypeColors(type);
  const iconProps = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (type) {
    case "video":
      return (
        <svg {...iconProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <polygon points="10,9 10,15 15,12" fill={color} stroke="none" />
        </svg>
      );
    case "audio":
      return (
        <svg {...iconProps}>
          <line x1="6" y1="8" x2="6" y2="16" />
          <line x1="10" y1="5" x2="10" y2="19" />
          <line x1="14" y1="8" x2="14" y2="16" />
          <line x1="18" y1="10" x2="18" y2="14" />
        </svg>
      );
    case "image":
      return (
        <svg {...iconProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="8" cy="10" r="2" />
          <polyline points="21,15 16,10 9,17" />
        </svg>
      );
    case "text":
      return (
        <svg {...iconProps}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
      );
    default:
      return (
        <svg {...iconProps}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
  }
}

export function AssetPreviewModal({
  asset: initialAsset,
  isOpen,
  onClose,
  onDelete,
  showDelete = true,
  outputLabel,
  outputVersion,
  textContent,
  assets,
  initialAssetIndex = 0,
  projectId: projectIdProp,
  assetId,
}: AssetPreviewModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Multi-asset navigation state
  const [currentIndex, setCurrentIndex] = useState(initialAssetIndex);
  const hasMultipleAssets = assets && assets.length > 1;

  // Deep-link support: fetch asset by ID if not provided directly
  const shouldFetchById = assetId && !initialAsset && !hasMultipleAssets;
  const {
    asset: fetchedAsset,
    isLoading: isFetchingAsset,
    error: fetchError,
  } = useAssetPreview(shouldFetchById ? assetId : null, {
    expectedProjectId: projectIdProp,
  });

  // Current asset - either from assets array, single asset prop, or fetched by ID
  const asset = hasMultipleAssets
    ? assets[currentIndex]
    : (initialAsset ?? fetchedAsset);

  const { metadata } = useAssetMetadata(asset);
  const [videoPlaybackError, setVideoPlaybackError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [useFallbackImage, setUseFallbackImage] = useState(false);
  const { data: auth } = useSignedCookies(asset?.projectId);

  // Audio playback state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // Timeout ref for thumbnail loading - fall back to full image after 3 seconds
  const thumbnailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch fresh derived assets when modal opens to get latest thumbnail data
  // This handles the case where the asset was fetched before thumbnail was ready
  const isVisualAsset =
    asset?.mimeType?.startsWith("image/") ||
    asset?.mimeType?.startsWith("video/");

  const derivedAssetsQuery = useQuery({
    queryKey: ["derived-assets", asset?.id],
    queryFn: async () => {
      if (!asset?.id) return [];
      const result = await listDerivedAssets({
        path: { assetId: asset.id },
        throwOnError: true,
      });
      return (result?.data?.data as Array<Record<string, unknown>>) || [];
    },
    enabled: isOpen && !!asset?.id && isVisualAsset,
    staleTime: 30000, // 30 seconds - allow refetch when modal reopens
  });

  // Reset index when modal opens or assets change
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialAssetIndex);
    }
  }, [isOpen, initialAssetIndex]);

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (hasMultipleAssets) {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : assets.length - 1));
      setImageLoaded(false);
    }
  }, [hasMultipleAssets, assets?.length]);

  const goToNext = useCallback(() => {
    if (hasMultipleAssets) {
      setCurrentIndex((prev) => (prev < assets.length - 1 ? prev + 1 : 0));
      setImageLoaded(false);
    }
  }, [hasMultipleAssets, assets?.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !hasMultipleAssets) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hasMultipleAssets, goToPrevious, goToNext]);

  // Resolve asset URL using clean CloudFront domain (cookies provide auth)
  const resolvedAssetUrl = useMemo(() => {
    if (!asset?.downloadUrl) return undefined;
    if (!auth?.domain) return asset.downloadUrl;
    try {
      const urlObj = new URL(asset.downloadUrl);
      return buildCloudFrontUrl(auth.domain, urlObj.pathname);
    } catch {
      return asset.downloadUrl;
    }
  }, [asset?.downloadUrl, auth?.domain]);

  // Resolve thumbnail URL - ALWAYS use thumbnail path for images
  // The useFallbackImage state controls whether we show thumbnail or full image
  const resolvedThumbnailUrl = useMemo(() => {
    if (!asset) {
      return undefined;
    }

    // Get CDN domain - use auth if available, otherwise fall back to env var
    const cdnDomain = auth?.domain || import.meta.env.VITE_CDN_BASE_URL;

    // For visual assets, ALWAYS return thumbnail URL (never fall back to full image here)
    if (cdnDomain && isVisualAsset) {
      return `https://${cdnDomain}/projects/${asset.projectId}/${asset.id}/thumbnail.jpg`;
    }

    // For non-visual assets or if no CDN domain, try resolveThumbnailUrl
    const assetWithFreshDerived = derivedAssetsQuery.data
      ? {
          ...asset,
          derivedAssets: derivedAssetsQuery.data as Asset["derivedAssets"],
        }
      : asset;

    return resolveThumbnailUrl(
      assetWithFreshDerived,
      asset.projectId,
      auth ?? null,
    );
  }, [asset, auth, derivedAssetsQuery.data, isVisualAsset]);

  // Reset state when asset changes
  // Track previous asset ID to prevent StrictMode double-reset
  const prevAssetIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevAssetIdRef.current !== asset?.id) {
      setVideoPlaybackError(false);
      setImageLoaded(false);
      setUseFallbackImage(false);
      setIsAudioPlaying(false);
      setAudioCurrentTime(0);
      setAudioDuration(0);
      prevAssetIdRef.current = asset?.id;

      // Clear any existing timeout
      if (thumbnailTimeoutRef.current) {
        clearTimeout(thumbnailTimeoutRef.current);
        thumbnailTimeoutRef.current = null;
      }
    }
  }, [asset?.id]);

  // Audio element setup
  const isAudioAsset = asset?.mimeType?.startsWith("audio/");
  useEffect(() => {
    if (!isOpen || !isAudioAsset || !resolvedAssetUrl) return;

    const audio = new Audio();
    audio.crossOrigin = "use-credentials";
    audio.preload = "none";
    audio.src = resolvedAssetUrl;
    audioRef.current = audio;

    const handleLoadedMetadata = () => setAudioDuration(audio.duration);
    const handleTimeUpdate = () => setAudioCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsAudioPlaying(false);
      setAudioCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, [isOpen, isAudioAsset, resolvedAssetUrl]);

  const handleAudioPlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play();
      setIsAudioPlaying(true);
    }
  }, [isAudioPlaying]);

  const handleAudioSeek = useCallback((time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setAudioCurrentTime(time);
  }, []);

  // Start thumbnail loading timeout when modal opens with an image
  // Falls back to full image if thumbnail doesn't load within 3 seconds
  useEffect(() => {
    const assetType = asset?.mimeType ? getAssetType(asset.mimeType) : null;

    if (
      isOpen &&
      asset &&
      assetType === "image" &&
      !useFallbackImage &&
      !imageLoaded
    ) {
      // Start 3-second timeout for thumbnail loading
      thumbnailTimeoutRef.current = setTimeout(() => {
        if (!imageLoaded) {
          setUseFallbackImage(true);
        }
      }, 3000);

      return () => {
        if (thumbnailTimeoutRef.current) {
          clearTimeout(thumbnailTimeoutRef.current);
          thumbnailTimeoutRef.current = null;
        }
      };
    }
  }, [isOpen, asset, useFallbackImage, imageLoaded]);

  // Handle image load complete - clear timeout and mark as loaded
  const handleImageLoad = useCallback(() => {
    // Clear timeout since image loaded successfully
    if (thumbnailTimeoutRef.current) {
      clearTimeout(thumbnailTimeoutRef.current);
      thumbnailTimeoutRef.current = null;
    }
    setImageLoaded((prev) => (prev ? prev : true));
  }, []);

  // Handle image load error - fall back to full image if thumbnail fails
  const handleImageError = useCallback(() => {
    if (!useFallbackImage) {
      setUseFallbackImage(true);
      setImageLoaded(false);
    }
  }, [useFallbackImage]);

  // Handle video playback error - memoized to prevent VideoPlayer re-renders
  const handleVideoError = useCallback(() => {
    setVideoPlaybackError(true);
  }, []);

  // Handle text-only preview (no asset)
  const isTextOnly = !asset && textContent !== undefined;

  // Render loading state for deep-linked assets
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mb-4" />
      <p className="text-sm text-muted-foreground">Loading asset...</p>
    </div>
  );

  // Render error state for deep-linked assets
  const renderErrorState = (error: AssetPreviewError) => {
    const errorConfig = {
      "not-found": {
        icon: AlertCircle,
        title: "Asset not found",
        message: "This asset no longer exists or may have been deleted.",
        testId: "asset-preview-error-not-found",
      },
      "no-access": {
        icon: Lock,
        title: "Access denied",
        message: "You don't have permission to view this asset.",
        testId: "asset-preview-error-no-access",
      },
      "invalid-id": {
        icon: AlertCircle,
        title: "Invalid link",
        message: "This asset link is malformed or invalid.",
        testId: "asset-preview-error-invalid-id",
      },
      "wrong-project": {
        icon: AlertCircle,
        title: "Asset not in this project",
        message: "This asset belongs to a different project.",
        testId: "asset-preview-error-wrong-project",
      },
    };

    const config = errorConfig[error!] || errorConfig["not-found"];
    const IconComponent = config.icon;

    return (
      <div
        className="flex flex-col items-center justify-center py-16 px-8 text-center"
        data-testid={config.testId}
      >
        <div
          className="rounded-full p-3 mb-4"
          style={{
            background: "rgba(var(--color-primitive-red-500-rgb), 0.1)",
          }}
        >
          <IconComponent className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="text-lg font-medium mb-2">{config.title}</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          {config.message}
        </p>
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </div>
    );
  };

  // Show loading/error states for deep-linked assets
  if (shouldFetchById) {
    if (isFetchingAsset) {
      return (
        <Dialog open={isOpen}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto p-4"
            style={{ width: "480px", maxWidth: "90vw" }}
            hideCloseButton
            onEscapeKeyDown={() => onClose()}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="sr-only">Loading asset</DialogTitle>
              <DialogDescription className="sr-only">
                Loading asset preview
              </DialogDescription>
            </DialogHeader>
            {renderLoadingState()}
          </DialogContent>
        </Dialog>
      );
    }

    if (fetchError) {
      return (
        <Dialog open={isOpen}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto p-4"
            style={{ width: "480px", maxWidth: "90vw" }}
            hideCloseButton
            onEscapeKeyDown={() => onClose()}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="sr-only">Error loading asset</DialogTitle>
              <DialogDescription className="sr-only">
                Unable to load asset preview
              </DialogDescription>
            </DialogHeader>
            {renderErrorState(fetchError)}
          </DialogContent>
        </Dialog>
      );
    }
  }

  if (!asset && !isTextOnly) return null;

  const assetType = isTextOnly ? "text" : getAssetType(asset!.mimeType);

  const handleDownload = () => {
    if (isTextOnly && textContent) {
      // Download text as a file
      const blob = new Blob([textContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `output-${outputVersion || "text"}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (asset && resolvedAssetUrl) {
      downloadAsset(resolvedAssetUrl, asset.filename);
    }
  };

  // Navigate to source workflow
  const handleGoToWorkflow = () => {
    if (asset?.sourceWorkflowId) {
      onClose();
      navigate(`/workflows/${asset.sourceWorkflowId}`);
    }
  };

  // Copy text to clipboard
  const handleCopyText = async () => {
    if (textContent) {
      await navigator.clipboard.writeText(textContent);
    }
  };

  const renderPreview = () => {
    // Max height for images/videos: 16:9 aspect ratio at content width (~808px = 455px height)
    const mediaMaxHeight = "455px";

    switch (assetType) {
      case "image": {
        // Compute thumbnail URL directly here - we KNOW it's an image at this point
        // Extract domain from the asset's download URL or use auth/env
        const cdnDomain = auth?.domain || import.meta.env.VITE_CDN_BASE_URL;
        let imageThumbnailUrl: string | undefined;

        if (cdnDomain && asset!.projectId) {
          // Construct thumbnail URL directly
          imageThumbnailUrl = `https://${cdnDomain}/projects/${asset!.projectId}/${asset!.id}/thumbnail.jpg`;
        } else if (asset!.downloadUrl) {
          // Try to extract domain from downloadUrl and construct thumbnail path
          try {
            const downloadUrlObj = new URL(asset!.downloadUrl);
            imageThumbnailUrl = `${downloadUrlObj.origin}/projects/${asset!.projectId}/${asset!.id}/thumbnail.jpg`;
          } catch {
            // Fall back to resolvedThumbnailUrl or full image
            imageThumbnailUrl = resolvedThumbnailUrl;
          }
        }

        // Final URL: use thumbnail unless we're in fallback mode or no thumbnail URL
        const imageDisplayUrl =
          useFallbackImage || !imageThumbnailUrl
            ? resolvedAssetUrl
            : imageThumbnailUrl;

        return (
          <div className="w-full overflow-hidden">
            <div
              className="w-full flex items-center justify-center relative"
              style={{
                background:
                  "rgba(var(--color-primitive-neutral-1000-rgb), 0.2)",
                height: mediaMaxHeight, // Fixed height - no layout shift
              }}
            >
              {/* Loading spinner - shown until image loads */}
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              )}
              {/* Image - use thumbnail if available, fall back to full image on error */}
              <img
                src={imageDisplayUrl}
                alt={asset!.filename}
                crossOrigin="use-credentials"
                className="w-full block object-contain transition-opacity duration-200"
                style={{
                  maxHeight: mediaMaxHeight,
                  opacity: imageLoaded ? 1 : 0,
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {/* Navigation arrows for multiple images */}
              {hasMultipleAssets && (
                <>
                  {/* Previous button */}
                  <button
                    type="button"
                    onClick={goToPrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all"
                    style={{
                      background:
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.5)",
                      color: "var(--color-primitive-neutral-0)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.5)";
                    }}
                    title="Previous image (←)"
                  >
                    <ChevronLeft style={{ width: "24px", height: "24px" }} />
                  </button>
                  {/* Next button */}
                  <button
                    type="button"
                    onClick={goToNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all"
                    style={{
                      background:
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.5)",
                      color: "var(--color-primitive-neutral-0)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.5)";
                    }}
                    title="Next image (→)"
                  >
                    <ChevronRight style={{ width: "24px", height: "24px" }} />
                  </button>
                  {/* Image counter */}
                  <div
                    className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-sm"
                    style={{
                      background:
                        "rgba(var(--color-primitive-neutral-1000-rgb), 0.6)",
                      color: "var(--color-primitive-neutral-0)",
                    }}
                  >
                    {currentIndex + 1} / {assets.length}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      }

      case "video":
        if (videoPlaybackError) {
          const formatLabel =
            VIDEO_FORMAT_LABELS[asset!.mimeType] ||
            asset!.mimeType.replace("video/", "").toUpperCase();
          return (
            <div className="w-full overflow-hidden">
              <div className="text-center space-y-3 p-8">
                <FileVideo className="h-12 w-12 text-muted-foreground mx-auto" />
                <div className="space-y-1">
                  <p className="font-medium">Preview unavailable</p>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    {formatLabel} files use a codec your browser cannot decode.
                    Download to play in VLC, QuickTime, or a video editor.
                  </p>
                </div>
                <Button onClick={handleDownload} variant="default">
                  <Download className="mr-2 h-4 w-4" />
                  Download {formatLabel} ({formatFileSize(asset!.sizeBytes)})
                </Button>
              </div>
            </div>
          );
        }
        return (
          <div className="w-full overflow-hidden">
            <div
              className="w-full flex items-center justify-center"
              style={{
                background:
                  "rgba(var(--color-primitive-neutral-1000-rgb), 0.2)",
                height: mediaMaxHeight, // Fixed height - no layout shift
              }}
            >
              <VideoPlayer
                key={asset!.id}
                src={resolvedAssetUrl || ""}
                className="h-full max-w-full"
                style={{ maxHeight: mediaMaxHeight }}
                onError={handleVideoError}
              />
            </div>
          </div>
        );

      case "audio": {
        const audioProgress =
          audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0;
        const formatTime = (seconds: number) => {
          const mins = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          return `${mins}:${secs.toString().padStart(2, "0")}`;
        };

        return (
          <div className="w-full overflow-hidden">
            <div
              className="w-full"
              style={{
                padding: "calc(var(--spacing) * 8) calc(var(--spacing) * 4)",
              }}
            >
              <div className="flex items-center gap-4">
                {/* Play/Pause button */}
                <button
                  type="button"
                  onClick={handleAudioPlayPause}
                  disabled={!resolvedAssetUrl}
                  aria-label={isAudioPlaying ? "Pause audio" : "Play audio"}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-[var(--color-primitive-blue-500)] text-white flex-shrink-0 transition-all hover:bg-[var(--color-interactive-cobalt-bghover)] hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAudioPlaying ? (
                    <Pause className="w-5 h-5" fill="currentColor" />
                  ) : (
                    <Play className="w-5 h-5" fill="currentColor" />
                  )}
                </button>

                {/* Waveform */}
                <div className="flex-1 min-w-0 h-12">
                  {asset?.id && (asset?.projectId || projectIdProp) ? (
                    <WaveformMini
                      assetId={asset.id}
                      projectId={asset.projectId || projectIdProp!}
                      progress={audioProgress}
                      currentTime={audioCurrentTime}
                      duration={audioDuration}
                      onSeek={handleAudioSeek}
                      hideCurrentTime
                      hideDuration
                    />
                  ) : (
                    <div className="h-12 bg-white/5 rounded" />
                  )}
                </div>

                {/* Time display */}
                <div
                  className="flex-shrink-0 text-sm font-mono"
                  style={{ color: "var(--color-primitive-neutral-175)" }}
                >
                  {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "text":
        return (
          <div className="w-full overflow-hidden">
            <div
              className="w-full rounded overflow-auto"
              style={{
                padding: "calc(var(--spacing) * 4)",
                background: "rgba(var(--color-primitive-amber-600-rgb), 0.08)",
                border:
                  "1px solid rgba(var(--color-primitive-amber-600-rgb), 0.2)",
                maxHeight: "400px",
              }}
            >
              <pre
                className="text-sm font-mono whitespace-pre-wrap break-words"
                style={{ color: "var(--color-primitive-neutral-150)" }}
              >
                {textContent}
              </pre>
            </div>
          </div>
        );

      default:
        return (
          <div className="w-full overflow-hidden">
            <div className="text-center p-8">
              <p className="text-muted-foreground mb-4">
                Preview not available for this file type
              </p>
              <Button onClick={handleDownload} variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Download File
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto p-4"
        style={{ width: "840px", maxWidth: "90vw" }}
        hideCloseButton
        onEscapeKeyDown={() => onClose()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            {/* Left side: Type icon + label */}
            <div className="flex items-center gap-2">
              {(() => {
                const { rgb } = getTypeColors(assetType);
                return (
                  <span
                    className="inline-flex items-center justify-center"
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "var(--radius-default)",
                      background: `rgba(${rgb}, 0.15)`,
                    }}
                  >
                    {getTypeIcon(assetType)}
                  </span>
                );
              })()}
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--color-primitive-neutral-150)",
                }}
              >
                {outputLabel ||
                  (outputVersion !== undefined
                    ? `Output #${outputVersion}`
                    : assetType.charAt(0).toUpperCase() + assetType.slice(1))}
              </span>
            </div>
            {/* Header action buttons */}
            <div className="flex items-center gap-1">
              {/* Copy button for text - grey borderless style */}
              {isTextOnly && textContent && (
                <button
                  type="button"
                  onClick={handleCopyText}
                  title="Copy to clipboard"
                  className="inline-flex items-center justify-center"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "var(--radius-default)",
                    transition: "all 0.15s ease",
                    background: "transparent",
                    border: "none",
                    color: "var(--color-interactive-utility-text)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-interactive-utility-bghover)";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utility-texthover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utility-text)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "scale(0.95)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <Copy style={{ width: "18px", height: "18px" }} />
                </button>
              )}
              {/* Download button - borderless with hover highlight */}
              <button
                type="button"
                onClick={handleDownload}
                title="Download"
                className="inline-flex items-center justify-center"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "var(--radius-default)",
                  transition: "all 0.15s ease",
                  background: "transparent",
                  border: "none",
                  color: "var(--color-interactive-utility-text)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "var(--color-interactive-utility-bghover)";
                  e.currentTarget.style.color =
                    "var(--color-interactive-utility-texthover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color =
                    "var(--color-interactive-utility-text)";
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "scale(0.95)";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <Download style={{ width: "18px", height: "18px" }} />
              </button>
              {/* Go to Workflow button - only shown when asset has source workflow */}
              {asset?.sourceWorkflowId && (
                <button
                  type="button"
                  onClick={handleGoToWorkflow}
                  title={t("assets.actions.goToWorkflow")}
                  className="inline-flex items-center justify-center"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "var(--radius-default)",
                    transition: "all 0.15s ease",
                    background: "transparent",
                    border: "none",
                    color: "var(--color-interactive-utility-text)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-interactive-utility-bghover)";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utility-texthover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utility-text)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "scale(0.95)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <Workflow style={{ width: "18px", height: "18px" }} />
                </button>
              )}
              {/* Delete button - borderless, red highlight on hover */}
              {showDelete && onDelete && asset && (
                <button
                  type="button"
                  onClick={() => {
                    onDelete(asset);
                    onClose();
                  }}
                  title="Delete"
                  className="inline-flex items-center justify-center"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "var(--radius-default)",
                    transition: "all 0.15s ease",
                    background: "transparent",
                    border: "none",
                    color: "var(--color-interactive-utility-text)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-interactive-utilitydestructive-bghover)";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utilitydestructive-texthover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color =
                      "var(--color-interactive-utility-text)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "scale(0.95)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <Trash2 style={{ width: "18px", height: "18px" }} />
                </button>
              )}
              {/* Close button - borderless with hover highlight */}
              <button
                type="button"
                onClick={onClose}
                title="Close"
                className="inline-flex items-center justify-center"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "var(--radius-default)",
                  transition: "all 0.15s ease",
                  background: "transparent",
                  border: "none",
                  color: "var(--color-interactive-utility-text)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "var(--color-interactive-utility-bghover)";
                  e.currentTarget.style.color =
                    "var(--color-interactive-utility-texthover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color =
                    "var(--color-interactive-utility-text)";
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "scale(0.95)";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <X style={{ width: "18px", height: "18px" }} />
              </button>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Asset preview and details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Preview */}
          {renderPreview()}

          {/* Compact Details Grid */}
          <div
            className="pt-2 border-t"
            style={{
              borderColor: "rgba(var(--color-primitive-neutral-0-rgb), 0.1)",
            }}
          >
            <div
              className="grid gap-x-6 gap-y-1"
              style={{
                gridTemplateColumns: "auto 1fr auto 1fr",
                fontSize: "12px",
              }}
            >
              {isTextOnly ? (
                <>
                  {/* Text-only details */}
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Type
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-150)" }}>
                    text/plain
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Characters
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-150)" }}>
                    {textContent?.length.toLocaleString() || 0}
                  </span>
                </>
              ) : (
                <>
                  {/* Row 1: Filename & Size */}
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Filename
                  </span>
                  <span
                    className="truncate"
                    style={{ color: "var(--color-primitive-neutral-150)" }}
                  >
                    <AssetFilename
                      fileName={asset!.filename}
                      maxWidth="max-w-[200px]"
                    />
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Size
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-150)" }}>
                    {formatFileSize(asset!.sizeBytes)}
                  </span>

                  {/* Row 2: Type & Uploaded */}
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Type
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-150)" }}>
                    {asset!.mimeType}
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-300)" }}>
                    Uploaded
                  </span>
                  <span style={{ color: "var(--color-primitive-neutral-150)" }}>
                    {formatDateTime(asset!.createdAt)}
                  </span>

                  {/* Row 3: Dimensions & Aspect Ratio (if available) */}
                  {metadata?.dimensions && (
                    <>
                      <span
                        style={{ color: "var(--color-primitive-neutral-300)" }}
                      >
                        Dimensions
                      </span>
                      <span
                        style={{ color: "var(--color-primitive-neutral-150)" }}
                      >
                        {formatDimensions(metadata.dimensions)}
                      </span>
                      <span
                        style={{ color: "var(--color-primitive-neutral-300)" }}
                      >
                        Aspect
                      </span>
                      <span
                        style={{ color: "var(--color-primitive-neutral-150)" }}
                      >
                        {formatAspectRatio(metadata.dimensions.aspectRatio)}
                      </span>
                    </>
                  )}

                  {/* Row 4: Duration (if available) */}
                  {typeof metadata?.duration === "number" &&
                    isFinite(metadata.duration) && (
                      <>
                        <span
                          style={{
                            color: "var(--color-primitive-neutral-300)",
                          }}
                        >
                          Duration
                        </span>
                        <span
                          style={{
                            color: "var(--color-primitive-neutral-150)",
                          }}
                        >
                          {formatDuration(metadata.duration)}
                        </span>
                        <span></span>
                        <span></span>
                      </>
                    )}
                </>
              )}
            </div>

            {/* Asset ID - separate row, smaller (only for assets) */}
            {!isTextOnly && asset && (
              <div
                className="mt-1.5 pt-1.5 flex items-center gap-2"
                style={{
                  borderTop:
                    "1px solid var(--color-interactive-utility-bghover)",
                  fontSize: "10px",
                }}
              >
                <span style={{ color: "var(--color-primitive-neutral-375)" }}>
                  ID
                </span>
                <span
                  className="font-mono"
                  style={{ color: "var(--color-primitive-neutral-200)" }}
                >
                  {asset.id}
                </span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
